import { ActionIcon, Box, Button, Group, Skeleton, Table, Text, Tooltip } from "@mantine/core"
import { EyeIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { currencyFormat } from "../../../utils"
import { TableFrame } from "../../delivery/atoms"
import { ReasonTooltip } from "../../ui"
import classes from "./catalog.module.css"
import { addBlockReason, type PriceView } from "./price-labels"
import { ProductThumb } from "./ProductThumb"
import { QtyStepper } from "./QtyStepper"
import { StockLabel } from "./StockLabel"
import { cleanName, sellsWithoutStock } from "./utils"

export type ProductRow = {
  product: any
  view: PriceView
  cartQty: number
  stock: number
  minOrder: number
}

/** Columns the buyer may hide in «Баганууд»; the rest always show. */
export const TABLE_OPTIONAL_COLUMNS = ["brand", "stock", "min"] as const

type Props = {
  hiddenColumns?: string[]
  rows: ProductRow[]
  priceLoading: boolean
  showSupplier: boolean
  /** Sets the cart quantity of a row; 0 removes it. */
  onSetQty: (product: any, quantity: number, row: ProductRow) => void
  onOpen: (product: any) => void
}

const focusRow = (index: number) => {
  const next = document.querySelector<HTMLInputElement>(`input[data-qty-row="${index}"]`)
  next?.focus()
  next?.select()
}

/** Canvas OrdB_CatalogTable: typed quantity per row, Enter moves down, ↑/↓ change by one (B17). */
export function ProductTable({ hiddenColumns = [], rows, priceLoading, showSupplier, onSetQty, onOpen }: Props) {
  const { t } = useTranslation("order")
  const show = (key: (typeof TABLE_OPTIONAL_COLUMNS)[number]) => !hiddenColumns.includes(key)

  return (
    <TableFrame>
      <Table.ScrollContainer minWidth={1120}>
        <Table verticalSpacing="sm" highlightOnHover layout="fixed">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={72}>{t("catalog.col.action")}</Table.Th>
              <Table.Th>{t("catalog.col.product")}</Table.Th>
              {show("brand") && <Table.Th w={140}>{t("catalog.col.brand")}</Table.Th>}
              <Table.Th w={196} ta="right">
                {t("catalog.col.price")}
              </Table.Th>
              {show("stock") && <Table.Th w={150}>{t("catalog.col.stock")}</Table.Th>}
              {show("min") && (
                <Table.Th w={72} ta="right">
                  {t("catalog.col.min")}
                </Table.Th>
              )}
              <Table.Th w={156}>{t("catalog.col.qty")}</Table.Th>
              <Table.Th w={140} ta="right">
                {t("catalog.col.total")}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row, index) => {
              const { product, view, cartQty, stock, minOrder } = row
              const sellWithoutStock = sellsWithoutStock(product)
              const name = cleanName(typeof product.nameMon === "string" ? product.nameMon : "")
              const brand = cleanName(product.brand?.name || (typeof product.brand === "string" ? product.brand : ""))
              const loadingPrice = priceLoading && view.price === 0
              const reason = loadingPrice ? null : addBlockReason(t, { price: view.price, stock, minOrder, sellWithoutStock })
              const max = sellWithoutStock ? undefined : stock
              return (
                <Table.Tr key={`${product.supplierBusinessId}-${product.id}`}>
                  <Table.Td>
                    <Tooltip label={t("catalog.viewDetail")} withArrow>
                      <ActionIcon variant="subtle" color="gray" aria-label={t("catalog.viewDetail")} onClick={() => onOpen(product)}>
                        <EyeIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Box className={classes.tableThumb}>
                        <ProductThumb src={product.image} alt={name} iconSize={18} />
                      </Box>
                      <Box miw={0}>
                        <Text fz="var(--nw-fz-body)" fw={500} truncate title={name}>
                          {name || "-"}
                        </Text>
                        <Text className={classes.sku} translate="no">
                          {product.skuCode || "-"}
                          {showSupplier && product.supplierName ? ` · ${cleanName(product.supplierName)}` : ""}
                        </Text>
                      </Box>
                    </Group>
                  </Table.Td>
                  {show("brand") && (
                    <Table.Td>
                      <Text fz="var(--nw-fz-body)" truncate title={brand}>
                        {brand || "-"}
                      </Text>
                    </Table.Td>
                  )}
                  <Table.Td ta="right">
                    {loadingPrice ? (
                      <Skeleton h={18} w={90} ml="auto" />
                    ) : view.price > 0 ? (
                      <>
                        <Text className={classes.mono} fz="var(--nw-fz-body)" fw={700}>
                          {currencyFormat(view.price)}
                        </Text>
                        <Text className={classes.source} data-promo={view.sourceIsPromo || undefined} truncate="end" title={view.source}>
                          {view.source}
                        </Text>
                      </>
                    ) : view.unavailable ? (
                      <>
                        <Text className={classes.mono} fz="var(--nw-fz-body)" fw={700}>
                          -
                        </Text>
                        <Text className={classes.source} truncate="end" title={view.source}>
                          {view.source}
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text fz="var(--nw-fz-body)" fw={600} c="var(--nw-text-2)" truncate="end" title={t("catalog.priceUnknownHint")}>
                          {t("catalog.priceUnknown")}
                        </Text>
                        <Text className={classes.source} truncate="end" title={t("catalog.priceUnknownHint")}>
                          {t("catalog.priceUnknownHint")}
                        </Text>
                      </>
                    )}
                  </Table.Td>
                  {show("stock") && (
                    <Table.Td>
                      <StockLabel stock={stock} sellWithoutStock={sellWithoutStock} />
                    </Table.Td>
                  )}
                  {show("min") && (
                    <Table.Td ta="right">
                      <Text className={classes.mono} fz="var(--nw-fz-body)">
                        {minOrder}
                      </Text>
                    </Table.Td>
                  )}
                  <Table.Td>
                    {reason && cartQty === 0 ? (
                      <ReasonTooltip reason={reason}>
                        <Button variant="default" radius="md" h={38} fullWidth>
                          {t("catalog.unavailable")}
                        </Button>
                      </ReasonTooltip>
                    ) : (
                      <QtyStepper
                        value={cartQty}
                        min={0}
                        max={max}
                        disabled={loadingPrice}
                        label={t("catalog.qtyLabel")}
                        data-qty-row={index}
                        onEnter={() => focusRow(index + 1)}
                        onChange={(qty) => onSetQty(product, qty, row)}
                      />
                    )}
                  </Table.Td>
                  <Table.Td ta="right">
                    {cartQty > 0 && view.price > 0 ? (
                      <Text className={classes.mono} fz="var(--nw-fz-body)" fw={700}>
                        {currencyFormat(cartQty * view.price)}
                      </Text>
                    ) : (
                      <Text className={classes.mono} fz="var(--nw-fz-body)" c="var(--nw-text-3)">
                        {view.price > 0 ? currencyFormat(0) : "-"}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </TableFrame>
  )
}
